import React from "react";
import { Dialog, Disclosure, Popover, Transition } from "@headlessui/react";
import { FiMenu, FiChevronDown } from "react-icons/fi";
import { BiXCircle } from "react-icons/bi";
import i18n from "../locale/i18n";
import { useTranslation } from "react-i18next";
import { getHeaderMenu } from "../services/api";

const lngs = {
  en: { nativeName: "English" },
  ar: { nativeName: "عربي" },
};

const MAIN_SITE = "https://365exams.com";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Menu urls are stored the way the MVC site writes them: "~/Programs", "/News",
// or occasionally already absolute. Normalise all three onto the main site.
function resolveUrl(url) {
  if (!url) return MAIN_SITE;
  if (/^https?:\/\//i.test(url)) return url;
  return MAIN_SITE + "/" + url.replace(/^~?\//, "");
}

// The API returns a flat list carrying parentId. Rebuild the two levels this
// header renders, ordered by the rank the admin set.
function buildTree(items, isArabic) {
  const byRank = (a, b) => (a.rank ?? 0) - (b.rank ?? 0);
  // localizedName always comes back English regardless of the Lang header, so
  // pick the field ourselves - React already knows which language is active.
  const label = (i) =>
    (isArabic ? i.nameAR : i.name)?.trim() || i.name || i.localizedName || "";

  return items
    .filter((i) => !i.parentId)
    .sort(byRank)
    .map((parent) => ({
      id: parent.id,
      name: label(parent),
      href: resolveUrl(parent.url),
      children: items
        .filter((c) => c.parentId === parent.id)
        .sort(byRank)
        .map((c) => ({ id: c.id, name: label(c), href: resolveUrl(c.url) })),
    }));
}

// Used only if the menu request fails, so the header degrades to something
// usable instead of rendering an empty bar.
const FALLBACK_MENU = [
  {
    id: "company",
    name: "Company",
    href: `${MAIN_SITE}/OurCompany`,
    children: [
      { id: "about", name: "About Us", href: `${MAIN_SITE}/About` },
      { id: "team", name: "Our Team", href: `${MAIN_SITE}/Team` },
      { id: "services", name: "Services", href: `${MAIN_SITE}/Services` },
    ],
  },
  { id: "programs", name: "Programs", href: `${MAIN_SITE}/Programs`, children: [] },
  { id: "contact", name: "Contact", href: `${MAIN_SITE}/Contact`, children: [] },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [menu, setMenu] = React.useState([]);
  const { t } = useTranslation();

  const isArabic = i18n.resolvedLanguage === "ar";

  React.useEffect(() => {
    let cancelled = false;
    getHeaderMenu().then((items) => {
      if (cancelled) return;
      setMenu(items.length ? buildTree(items, isArabic) : FALLBACK_MENU);
    });
    return () => {
      cancelled = true;
    };
    // Rebuilt on language change so Arabic labels come from nameAR.
  }, [isArabic]);

  const handleChangeLanguage = () => {
    if (i18n.resolvedLanguage === Object.keys(lngs)[0]) {
      i18n.changeLanguage(Object.keys(lngs)[1]);
    } else {
      i18n.changeLanguage(Object.keys(lngs)[0]);
    }
    document.body.dir = i18n.dir();
  };

  return (
    <header className="sticky top-0 z-10 bg-slate-100 backdrop-filter backdrop-blur-md bg-opacity-30 border-b border-gray-200">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-8"
        aria-label="Global"
      >
        <div className="flex lg:flex-1">
          <a
            href={MAIN_SITE + "/"}
            target="_blank"
            rel="noreferrer"
            className="-m-1.5 p-1.5"
          >
            <span className="sr-only">365 Exams</span>
            <img className="h-12 w-auto" src="/logo-dark.png" alt="Company Logo" />
          </a>
        </div>

        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <FiMenu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <Popover.Group className="hidden lg:flex lg:gap-x-12">
          {menu.map((item) =>
            item.children.length ? (
              <Popover className="relative" key={item.id}>
                <Popover.Button className="flex items-center gap-x-1 text-sm font-semibold leading-6 text-gray-900">
                  {item.name}
                  <FiChevronDown
                    className="h-5 w-5 flex-none text-gray-400"
                    aria-hidden="true"
                  />
                </Popover.Button>

                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <Popover.Panel className="absolute -left-8 top-full z-10 mt-3 w-screen max-w-md overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5">
                    <div className="p-4">
                      {item.children.map((child) => (
                        <div
                          key={child.id}
                          className="group relative flex items-center gap-x-6 rounded-lg p-4 text-sm leading-6 hover:bg-gray-50"
                        >
                          <div className="flex-auto">
                            <a
                              href={child.href}
                              target="_blank"
                              rel="noreferrer"
                              className="block font-semibold text-gray-900"
                            >
                              {child.name}
                              <span className="absolute inset-0" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Popover.Panel>
                </Transition>
              </Popover>
            ) : (
              <a
                key={item.id}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold leading-6 text-gray-900"
              >
                {item.name}
              </a>
            )
          )}

          <button
            className="leading-6 text-gray-900 underline underline-offset-8 hover:no-underline opacity-70 hover:opacity-100"
            type="button"
            onClick={handleChangeLanguage}
          >
            {t("header.lng")}
          </button>
        </Popover.Group>

        <div className="hidden lg:flex lg:flex-1 lg:justify-end gap-4 lg:gap-6">
          <a
            href={`${MAIN_SITE}/StudentProfile`}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-semibold leading-6 text-gray-900"
          >
            {t("header.Profile")}
          </a>
        </div>
      </nav>

      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-10" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <a href={MAIN_SITE} target="_blank" rel="noreferrer" className="-m-1.5 p-1.5">
              <span className="sr-only">365 Exams</span>
              <img className="h-8 w-auto" src="/logo-dark.png" alt="Company Logo" />
            </a>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <BiXCircle className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {menu.map((item) =>
                  item.children.length ? (
                    <Disclosure as="div" className="-mx-3" key={item.id}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50">
                            {item.name}
                            <FiChevronDown
                              className={classNames(
                                open ? "rotate-180" : "",
                                "h-5 w-5 flex-none"
                              )}
                              aria-hidden="true"
                            />
                          </Disclosure.Button>
                          <Disclosure.Panel className="mt-2 space-y-2">
                            {item.children.map((child) => (
                              <Disclosure.Button
                                key={child.id}
                                as="a"
                                href={child.href}
                                target="_blank"
                                rel="noreferrer"
                                className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                              >
                                {child.name}
                              </Disclosure.Button>
                            ))}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  ) : (
                    <a
                      key={item.id}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    >
                      {item.name}
                    </a>
                  )
                )}

                <button
                  className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50 w-full text-start"
                  type="button"
                  onClick={handleChangeLanguage}
                >
                  {t("header.lng")}
                </button>
              </div>

              <div className="py-6">
                <a
                  href={`${MAIN_SITE}/StudentProfile`}
                  target="_blank"
                  rel="noreferrer"
                  className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                >
                  {t("header.Profile")}
                </a>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
};

export default Header;
